import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { useLiquidateVenue } from "./use-liquidate-venue";
import { resolvePayoutError } from "./resolve-payout-error";
import { formatMoney, moneyFromCentsString } from "./money";
import { LIQUIDATE_REASON_MAX_LENGTH, LIQUIDATE_REASON_MIN_LENGTH } from "./constants";
import type { LiquidateResponse, PendingBalanceItem } from "./types";

interface LiquidateVenueDialogProps {
  readonly item: PendingBalanceItem | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/** A 200 with nothing written off means another settlement won the race. */
function isRaceLostNoop(result: LiquidateResponse): boolean {
  return result.accrualCount === 0 && Number(result.liquidatedCents) === 0;
}

export function LiquidateVenueDialog({ item, open, onOpenChange }: LiquidateVenueDialogProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LiquidateResponse | null>(null);
  const liquidate = useLiquidateVenue();

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setResult(null);
    }
  }, [open]);

  if (!item) return null;

  const handleClose = (next: boolean) => {
    if (!next && liquidate.isPending) return;
    onOpenChange(next);
  };

  const reasonValid = reason.trim().length >= LIQUIDATE_REASON_MIN_LENGTH;

  const handleSubmit = () => {
    if (!reasonValid) return;
    setError(null);
    liquidate.mutate(
      { venueId: item.venueId, currency: item.currency, reason },
      {
        onSuccess: (res) => setResult(res),
        onError: (err) => setError(resolvePayoutError(err, t)),
      },
    );
  };

  const venueLabel = item.venueName ?? item.venueId;
  const currency = item.currency.toUpperCase();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="liquidate-venue-dialog">
        <DialogHeader>
          <DialogTitle>{t("finance.liquidate.title", { venue: venueLabel, currency })}</DialogTitle>
          <DialogDescription>{t("finance.liquidate.description")}</DialogDescription>
        </DialogHeader>

        {result === null ? (
          <div className="space-y-4 py-4">
            <p className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 font-sans text-sm text-destructive">
              {t("finance.liquidate.warning", {
                amount: formatMoney(moneyFromCentsString(item.totalPendingCents, item.currency)),
                currency,
              })}
            </p>
            <div className="space-y-1.5">
              <label
                htmlFor="liquidate-reason"
                className="font-sans text-sm font-medium text-foreground"
              >
                {t("finance.liquidate.reasonLabel")}
              </label>
              <Textarea
                id="liquidate-reason"
                data-testid="liquidate-reason-textarea"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                minLength={LIQUIDATE_REASON_MIN_LENGTH}
                maxLength={LIQUIDATE_REASON_MAX_LENGTH}
                rows={3}
                placeholder={t("finance.liquidate.reasonPlaceholder")}
                disabled={liquidate.isPending}
              />
              <p className="font-sans text-xs text-muted-foreground">
                {t("finance.liquidate.reasonHint")}
              </p>
            </div>

            {error && (
              <p
                data-testid="liquidate-error"
                role="alert"
                className="font-sans text-sm font-medium text-destructive"
              >
                {error}
              </p>
            )}
          </div>
        ) : (
          <div data-testid="liquidate-result" className="space-y-2 py-4">
            {isRaceLostNoop(result) ? (
              <p data-testid="liquidate-noop" className="font-sans text-sm text-muted-foreground">
                {t("finance.liquidate.alreadySettled")}
              </p>
            ) : (
              <p className="font-sans text-sm text-foreground">
                {t("finance.liquidate.success", {
                  amount: formatMoney(
                    moneyFromCentsString(result.liquidatedCents, result.currency),
                  ),
                  currency,
                  count: result.accrualCount,
                })}
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {result === null ? (
            <>
              <Button
                data-testid="liquidate-cancel-button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={liquidate.isPending}
              >
                {t("finance.liquidate.cancel")}
              </Button>
              <Button
                data-testid="confirm-liquidate-button"
                variant="destructive"
                onClick={handleSubmit}
                disabled={liquidate.isPending || !reasonValid}
              >
                {liquidate.isPending
                  ? t("finance.liquidate.pending")
                  : t("finance.liquidate.confirm")}
              </Button>
            </>
          ) : (
            <Button data-testid="liquidate-done-button" onClick={() => handleClose(false)}>
              {t("finance.liquidate.done")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
